import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import {
  fetchHubupGroupFormAnnuaire,
  syncHubupGroupFormAnnuaire,
} from '../../api/hubup';

function formatParisDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      timeZone: 'Europe/Paris',
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

export default function HubupFormAnnuaire() {
  const { groupId } = useParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    if (!groupId) return;
    setError(null);
    setLoading(true);
    try {
      const res = await fetchHubupGroupFormAnnuaire(groupId);
      setItems(Array.isArray(res?.data?.items) ? res.data.items : []);
    } catch (e) {
      setError(e?.message || 'Impossible de charger l’annuaire.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => {
    load();
  }, [load]);

  const onSync = async () => {
    if (!groupId || syncing) return;
    setSyncing(true);
    setSyncResult(null);
    setError(null);
    try {
      const res = await syncHubupGroupFormAnnuaire(groupId);
      const d = res?.data ?? {};
      setSyncResult({
        scannedForms: d.scannedForms ?? 0,
        scannedResponses: d.scannedResponses ?? 0,
        upserted: d.upserted ?? 0,
      });
      await load();
    } catch (e) {
      setError(e?.message || 'Synchronisation impossible.');
    } finally {
      setSyncing(false);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (row) =>
        String(row.email ?? '').toLowerCase().includes(q) ||
        String(row.hubupUserId ?? '').toLowerCase().includes(q) ||
        String(row.formName ?? '').toLowerCase().includes(q) ||
        String(row.displayName ?? '').toLowerCase().includes(q),
    );
  }, [items, query]);

  return (
    <>
      <Helmet>
        <title>Annuaire — HubUP</title>
      </Helmet>
      <div className="px-4 md:px-6 lg:px-8 py-6 max-w-6xl mx-auto space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Annuaire</h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Liste des personnes repérées dans vos formulaires : e-mail et identifiant
              HubUP associé. Se met à jour au fil des nouvelles réponses.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            disabled={syncing || loading}
            onClick={onSync}
            className="shrink-0"
          >
            {syncing ? (
              <>
                <Icon name="Loader2" className="w-4 h-4 mr-2 animate-spin" />
                Synchronisation…
              </>
            ) : (
              <>
                <Icon name="RefreshCw" className="w-4 h-4 mr-2" />
                Mettre à jour l’annuaire
              </>
            )}
          </Button>
        </div>

        {syncResult ? (
          <div
            className="rounded-lg border border-border bg-muted/40 px-4 py-3 text-sm text-foreground"
            role="status"
          >
            {syncResult.upserted > 0
              ? `${syncResult.upserted} contact(s) ajouté(s) ou mis à jour.`
              : 'Aucun nouveau contact à ajouter.'}
          </div>
        ) : null}

        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <label className="flex-1 relative">
            <span className="sr-only">Rechercher</span>
            <Icon
              name="Search"
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
            />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher par e-mail, nom ou formulaire…"
              className="w-full pl-9 pr-3 py-2 rounded-md border border-input bg-background text-sm"
            />
          </label>
          <p className="text-sm text-muted-foreground tabular-nums shrink-0">
            {filtered.length} contact{filtered.length !== 1 ? 's' : ''}
          </p>
        </div>

        {error ? (
          <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        ) : null}

        {loading ? (
          <div className="text-sm text-muted-foreground py-8 text-center">Chargement…</div>
        ) : filtered.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border py-12 text-center text-sm text-muted-foreground">
            Aucun contact pour l’instant. Utilisez « Mettre à jour l’annuaire » pour
            récupérer les réponses déjà enregistrées.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50 text-left">
                  <th className="px-4 py-3 font-medium">E-mail</th>
                  <th className="px-4 py-3 font-medium">ID HubUP</th>
                  <th className="px-4 py-3 font-medium hidden md:table-cell">Nom</th>
                  <th className="px-4 py-3 font-medium hidden lg:table-cell">Entreprise / formulaire</th>
                  <th className="px-4 py-3 font-medium hidden sm:table-cell">Dernière réponse</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr key={row.id} className="border-b border-border last:border-0">
                    <td className="px-4 py-3 font-mono text-xs sm:text-sm">{row.email}</td>
                    <td className="px-4 py-3 font-mono text-xs break-all">{row.hubupUserId}</td>
                    <td className="px-4 py-3 hidden md:table-cell">{row.displayName || '—'}</td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      {row.source === 'company_team' ? (
                        <span className="text-foreground">{row.formName || '—'}</span>
                      ) : (
                        <>
                          {row.formName || row.formId || '—'}
                          {row.messageId ? (
                            <div className="mt-0.5">
                              <Link
                                to={`/hubup/g/${groupId}/notifications/${row.messageId}/form-responses`}
                                className="text-xs text-primary hover:underline"
                              >
                                Voir les réponses
                              </Link>
                            </div>
                          ) : null}
                        </>
                      )}
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell whitespace-nowrap">
                      {formatParisDate(row.lastResponseAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
